import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

import {
  parseDevStackStatusSnapshot,
  type DevStackStatusSnapshot,
} from "@/lib/dev-stack-status";
import { HUB_DEV_STACK_RESTARTING_EVENT } from "@/lib/api";
import { hubDevStackHealthzUrl } from "@/lib/hubApiOrigin";

export type DevStackRestartFlags = Partial<
  Record<"erp-api" | "erp-ui" | "erp-vendor-ui", boolean>
>;

const POLL_MS = 1_000;
/** Consecutive /healthz 200 polls before clearing sticky fetch restart flags. */
const SUSTAINED_HEALTH_POLLS = 2;

function flagsFromSnapshot(snapshot: DevStackStatusSnapshot): DevStackRestartFlags {
  const flags: DevStackRestartFlags = {};
  for (const key of ["erp-api", "erp-ui", "erp-vendor-ui"] as const) {
    if (snapshot[key]?.phase === "restarting") {
      flags[key] = true;
    }
  }
  return flags;
}

function mergeRestartFlags(...sources: DevStackRestartFlags[]): DevStackRestartFlags {
  const merged: DevStackRestartFlags = {};
  for (const source of sources) {
    for (const key of ["erp-api", "erp-ui", "erp-vendor-ui"] as const) {
      if (source[key]) {
        merged[key] = true;
      }
    }
  }
  return merged;
}

/**
 * Restart banner for the **routed** stack (active branch), not the main-clone gen.ts file.
 * Polls `/dev-stack-status` and routed-stack `/healthz` (via {@link hubDevStackHealthzUrl}),
 * and listens for in-flight apiFetch retries so the banner appears before the API is fully down.
 */
export function useDevStackRestartFlags(): DevStackRestartFlags {
  const [pollFlags, setPollFlags] = useState<DevStackRestartFlags>({});
  const [fetchFlags, setFetchFlags] = useState<DevStackRestartFlags>({});
  /** Sustained /healthz 200 — clears sticky fetch restart flags for erp-api. */
  const [apiHealthSustained, setApiHealthSustained] = useState(false);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const onFetchRestart = () => {
      setFetchFlags({ "erp-api": true });
    };

    window.addEventListener(HUB_DEV_STACK_RESTARTING_EVENT, onFetchRestart);
    return () => {
      window.removeEventListener(HUB_DEV_STACK_RESTARTING_EVENT, onFetchRestart);
    };
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    let cancelled = false;
    let healthyStreak = 0;

    const clearStickyApiRestartFlag = (
      setter: Dispatch<SetStateAction<DevStackRestartFlags>>,
    ) => {
      setter((current) => {
        if (!current["erp-api"]) {
          return current;
        }
        const next = { ...current };
        delete next["erp-api"];
        return next;
      });
    };

    const poll = async () => {
      try {
        const [statusResponse, healthResponse] = await Promise.all([
          fetch("/dev-stack-status", { cache: "no-store" }),
          fetch(hubDevStackHealthzUrl(), { cache: "no-store" }),
        ]);
        if (cancelled) {
          return;
        }

        let statusFlags: DevStackRestartFlags = {};
        if (statusResponse.ok) {
          const data: unknown = await statusResponse.json();
          statusFlags = flagsFromSnapshot(parseDevStackStatusSnapshot(data));
        }

        if (healthResponse.ok) {
          healthyStreak += 1;
          if (healthyStreak >= SUSTAINED_HEALTH_POLLS) {
            setApiHealthSustained(true);
          }
        } else {
          healthyStreak = 0;
          setApiHealthSustained(false);
        }

        setPollFlags(() => {
          const next: DevStackRestartFlags = { ...statusFlags };
          if (healthResponse.ok) {
            delete next["erp-api"];
          } else if (!statusFlags["erp-api"]) {
            next["erp-api"] = true;
          }
          return next;
        });

        if (healthResponse.ok) {
          clearStickyApiRestartFlag(setFetchFlags);
        }
      } catch {
        healthyStreak = 0;
        setApiHealthSustained(false);
        // control/API briefly unavailable — keep last flags
      }
    };

    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const merged = mergeRestartFlags(pollFlags, fetchFlags);
  if (apiHealthSustained) {
    delete merged["erp-api"];
  }
  return merged;
}

export function useDevStackApiReloading(): boolean {
  const flags = useDevStackRestartFlags();
  return flags["erp-api"] === true;
}

export { useDevStackRestartMessage } from "@/lib/useDevStackRestartMessage";
